import "./WSPortfolioActivities.scss";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import moment from "moment";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import FallbackTickerIcon from "../../../common/FallbackTickerIcon/FallbackTickerIcon";
import React, { useState, useMemo, useRef, useEffect } from "react";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "../../../../contexts/Auth";

const WSPortfolioActivity = ({ item, loading }) => {
  const { isBlurred } = useAuth();
  const [open, setOpen] = useState(true);
  const { series } = useParams();
  const [sortConfig, setSortConfig] = useState({
    key: "holdingChangeCategory",
    direction: "ascending",
  });
  const history = useHistory();

  const activitiesRef = useRef(null);
  const headerRef = useRef(null);
  const isSyncingScroll = useRef(false);

  // Helper function to categorize holding changes
  const getHoldingChangeCategory = (oldPrice, newPrice) => {
    if (oldPrice > 0 && newPrice === 0) return 1; // SELL
    if (oldPrice > newPrice && newPrice > 0) return 2; // REBALANCE DOWN
    if (newPrice > oldPrice && oldPrice > 0) return 3; // REBALANCE UP
    if (oldPrice === 0 && newPrice > 0) return 4; // BUY
    return 5; // HOLD (no change)
  };

  const sortedActivities = useMemo(() => {
    if (loading || !item.adjustments)
      return Array.from({ length: 5 }).fill(null);

    const sortableItems = [...(item.adjustments || [])];

    // Assign categories to each item
    sortableItems.forEach((item) => {
      item.holdingChangeCategory = getHoldingChangeCategory(
        item.oldPrice,
        item.newPrice,
      );
    });

    sortableItems.sort((a, b) => {
      // Primary sort by holdingChangeCategory (SELL → REBALANCE DOWN → REBALANCE UP → BUY → HOLD)
      if (sortConfig.key === "holdingChangeCategory") {
        // First sort by category
        if (a.holdingChangeCategory !== b.holdingChangeCategory) {
          return sortConfig.direction === "ascending"
            ? a.holdingChangeCategory - b.holdingChangeCategory
            : b.holdingChangeCategory - a.holdingChangeCategory;
        }

        // Secondary sort by impact (diff) - largest change first
        const aDiffAbs = Math.abs(a.diff);
        const bDiffAbs = Math.abs(b.diff);
        return bDiffAbs - aDiffAbs;
      }
      // AI Entry Price sorting
      else if (sortConfig.key === "ai_entry_price") {
        const aPrice = a.ai_entry_price ? parseFloat(a.ai_entry_price) : 0;
        const bPrice = b.ai_entry_price ? parseFloat(b.ai_entry_price) : 0;

        // Handle cases where price might be NaN
        const aValidPrice = isNaN(aPrice) ? 0 : aPrice;
        const bValidPrice = isNaN(bPrice) ? 0 : bPrice;

        return sortConfig.direction === "ascending"
          ? aValidPrice - bValidPrice
          : bValidPrice - aValidPrice;
      }
      // Impact sorting
      else if (sortConfig.key === "diff") {
        const aDiff = a[sortConfig.key] || 0;
        const bDiff = b[sortConfig.key] || 0;
        const aDiffAbs = Math.abs(aDiff);
        const bDiffAbs = Math.abs(bDiff);
        return sortConfig.direction === "ascending"
          ? aDiffAbs - bDiffAbs
          : bDiffAbs - aDiffAbs;
      }
      // Ticker sorting
      else if (sortConfig.key === "ticker") {
        const aTicker = a[sortConfig.key];
        const bTicker = b[sortConfig.key];

        // Handle null/undefined values - always put them at the end regardless of sort direction
        const aIsEmpty = !aTicker || aTicker === null || aTicker === undefined;
        const bIsEmpty = !bTicker || bTicker === null || bTicker === undefined;

        if (aIsEmpty && !bIsEmpty) {
          return 1; // Always put a (empty) after b (has value)
        }
        if (bIsEmpty && !aIsEmpty) {
          return -1; // Always put b (empty) after a (has value)
        }

        // If both are empty, they are equal
        if (aIsEmpty && bIsEmpty) {
          return 0;
        }

        // Normal alphabetical sorting for non-empty values
        const aKeyLower = aTicker.toLowerCase();
        const bKeyLower = bTicker.toLowerCase();
        const result = aKeyLower.localeCompare(bKeyLower);
        return sortConfig.direction === "ascending" ? result : -result;
      }
      // Sector sorting
      else if (sortConfig.key === "sector") {
        const aSector = a[sortConfig.key];
        const bSector = b[sortConfig.key];

        // Handle null/undefined values - always put them at the end regardless of sort direction
        const aIsEmpty = !aSector || aSector === null || aSector === undefined;
        const bIsEmpty = !bSector || bSector === null || bSector === undefined;

        if (aIsEmpty && !bIsEmpty) {
          return 1; // Always put a (empty) after b (has value)
        }
        if (bIsEmpty && !aIsEmpty) {
          return -1; // Always put b (empty) after a (has value)
        }

        // If both are empty, they are equal
        if (aIsEmpty && bIsEmpty) {
          return 0;
        }

        // Normal alphabetical sorting for non-empty values
        const aKeyLower = aSector.toLowerCase();
        const bKeyLower = bSector.toLowerCase();
        const result = aKeyLower.localeCompare(bKeyLower);
        return sortConfig.direction === "ascending" ? result : -result;
      }
      return 0;
    });

    return sortableItems;
  }, [item?.adjustments, sortConfig, loading]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };
  const navigateTicker = (portfolio, ticker) => {
    history.push(
      `/wealth-series/${series}/${portfolio}?ticker=${ticker}&blurred=${isBlurred}`,
    );
  };

  useEffect(() => {
    const handleScroll = (source, target) => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      target.scrollLeft = source.scrollLeft;
      isSyncingScroll.current = false;
    };

    const headerElement = headerRef.current;
    const activitiesElement = activitiesRef.current;

    const onHeaderScroll = () => handleScroll(headerElement, activitiesElement);
    const onActivitiesScroll = () =>
      handleScroll(activitiesElement, headerElement);

    if (headerElement && activitiesElement) {
      headerElement.addEventListener("scroll", onHeaderScroll);
      activitiesElement.addEventListener("scroll", onActivitiesScroll);
    }

    return () => {
      if (headerElement) {
        headerElement.removeEventListener("scroll", onHeaderScroll);
      }
      if (activitiesElement) {
        activitiesElement.removeEventListener("scroll", onActivitiesScroll);
      }
    };
  }, []);

  return (
    <div className="ws-portfolio-activity">
      <div
        className={`ws-portfolio-activity__header ${open ? "" : "closed"}`}
        onClick={() => setOpen(!open)}
      >
        {loading ? (
          <div className="ws-portfolio-activity__date">
            <BeautifulSkeleton width={150} height={20} />
            <BeautifulSkeleton width={20} height={20} />
          </div>
        ) : (
          <div className="ws-portfolio-activity__date">
            {moment(item.date).format("dddd, MMM D, YYYY")}
            <span>{item?.adjustments?.length}</span>
          </div>
        )}
        {open ? <RemoveIcon /> : <AddIcon />}
      </div>

      <div>
        <div
          ref={headerRef}
          className={`ws-portfolio-activity__table_header ${open ? "open" : "closed"}`}
        >
          <div className="ws-portfolio-activity__activity-portfolio">
            Portfolio
          </div>
          <div
            className="ws-portfolio-activity__activity-ticker"
            onClick={() => requestSort("ticker")}
          >
            Ticker{" "}
            {sortConfig.key === "ticker" &&
              (sortConfig.direction === "ascending" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              ))}
          </div>
          <div
            className="ws-portfolio-activity__activity-sector"
            onClick={() => requestSort("sector")}
          >
            Sector{" "}
            {sortConfig.key === "sector" &&
              (sortConfig.direction === "ascending" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              ))}
          </div>
          <div
            className="ws-portfolio-activity__activity-price"
            onClick={() => requestSort("ai_entry_price")}
          >
            AI Trade Price{" "}
            {sortConfig.key === "ai_entry_price" &&
              (sortConfig.direction === "ascending" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              ))}
          </div>
          <div
            className="ws-portfolio-activity__activity-change"
            onClick={() => requestSort("holdingChangeCategory")}
          >
            Holding Change{" "}
            {sortConfig.key === "holdingChangeCategory" &&
              (sortConfig.direction === "ascending" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              ))}
          </div>
          <div
            className="ws-portfolio-activity__activity-impact"
            onClick={() => requestSort("diff")}
          >
            Impact{" "}
            {sortConfig.key === "diff" &&
              (sortConfig.direction === "ascending" ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              ))}
          </div>
        </div>

        <div
          ref={activitiesRef}
          className={`ws-portfolio-activity__activities ${open ? "open" : "closed"}`}
        >
          {(loading
            ? Array.from({ length: 5 }).fill(null)
            : sortedActivities
          )?.map((activity, index) =>
            loading ? (
              <div key={index} className="ws-portfolio-activity__activity">
                <div className="ws-portfolio-activity__activity-portfolio">
                  <BeautifulSkeleton width={100} height={32} />
                </div>
                <div className="ws-portfolio-activity__activity-ticker">
                  <span className="ws-portfolio-activity__activity-ticker-icon">
                    <BeautifulSkeleton
                      variant="circular"
                      width={24}
                      height={24}
                    />
                    <BeautifulSkeleton
                      width={70}
                      height={32}
                      className="hidden-on-mobile"
                    />
                  </span>
                </div>
                <div className="ws-portfolio-activity__activity-sector">
                  <BeautifulSkeleton width={100} height={32} />
                </div>
                <div className="ws-portfolio-activity__activity-price">
                  <BeautifulSkeleton width={100} height={32} />
                </div>
                <div className="ws-portfolio-activity__activity-change">
                  <BeautifulSkeleton width={150} height={32} />
                </div>
                <div className={`ws-portfolio-activity__activity-impact`}>
                  <BeautifulSkeleton width={100} height={32} />
                </div>
              </div>
            ) : (
              <div key={index} className="ws-portfolio-activity__activity">
                <div
                  className={`ws-portfolio-activity__activity-portfolio ${isBlurred ? "blurred" : ""}`}
                >
                  {activity.portfolioName}
                </div>
                <div
                  className={`ws-portfolio-activity__activity-ticker ${isBlurred ? "blurred" : ""}`}
                  onClick={() =>
                    navigateTicker(activity.portfolioKey, activity.ticker)
                  }
                >
                  <span className="ws-portfolio-activity__activity-ticker-icon">
                    <FallbackTickerIcon
                      src={`https://assets.tradealgo.com/stock-logos/${activity.ticker}.png`}
                      ticker={activity.ticker}
                      className="ws-portfolio-activity__activity-icon"
                    />
                    {activity.ticker}
                  </span>
                </div>
                <div
                  className={`ws-portfolio-activity__activity-sector ${isBlurred ? "blurred" : ""}`}
                >
                  {activity.sector || "N/A"}
                </div>
                <div className="ws-portfolio-activity__activity-price">
                  {activity?.ai_entry_price
                    ? "$" + Number(activity.ai_entry_price).toFixed(2)
                    : "N/A"}
                </div>
                <div className="ws-portfolio-activity__activity-change">
                  <span
                    style={{ width: 50, textAlign: "right" }}
                    className="ws-portfolio-activity__activity-change--old"
                  >
                    {(activity.oldPrice * 100).toFixed(2)}%
                  </span>
                  <ArrowRightAltIcon className="ws-portfolio-activity__activity-change--icon" />
                  <span
                    style={{ width: 50, textAlign: "left" }}
                    className="ws-portfolio-activity__activity-change--new"
                  >
                    {(activity.newPrice * 100).toFixed(2)}%
                  </span>
                </div>
                <div className={`ws-portfolio-activity__activity-impact`}>
                  <span>
                    {(activity.diff * 100).toFixed(2) == 0
                      ? "0.00"
                      : activity.diff * 100 > 0
                        ? "+ " + Math.abs(activity.diff * 100).toFixed(2)
                        : "- " + Math.abs(activity.diff * 100).toFixed(2)}
                    %
                  </span>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default WSPortfolioActivity;
